import React from 'react';
import AppNav from '../components/app/AppNav';

const values = [
  { emoji: '👥', title: 'Community First', desc: 'We believe in the power of connected communities to create positive change.' },
  { emoji: '🤝', title: 'Trust & Safety', desc: 'Creating a safe, trustworthy platform where users feel comfortable connecting.' },
  { emoji: '💡', title: 'Innovation', desc: 'We continuously evolve to better serve the changing needs of communities.' },
  { emoji: '🌍', title: 'Inclusivity', desc: 'We design our platform to be accessible and welcoming to everyone.' },
];

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <AppNav />

      {/* Hero */}
      <div className="bg-indigo-600 text-white px-6 py-16 text-center">
        <p className="text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-3">About Us</p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">About Sahayy</h1>
        <p className="text-indigo-200 text-lg">The 🤝 people platform. Where communities become families.</p>
      </div>

      {/* Story */}
      <section className="px-6 py-14 max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">The Meaning Behind Our Name</h2>
        <p className="text-gray-600 leading-relaxed mb-5">
          <strong className="text-indigo-700">"Sahayy"</strong> originates from Sanskrit, where "Sahāya" in Hindi
          translates to <em>helper</em>, <em>supporter</em>, or <em>assistant</em>. It signifies someone who
          provides aid, support, or assistance to another — a helpful companion and friend in times of need.
        </p>

        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl px-6 py-5 mb-8">
          <div className="text-xl italic text-indigo-700 font-serif text-center mb-2">
            एकमेका सहाय्य करू अवघे धरू सुपंथ!!
          </div>
          <div className="text-sm text-gray-500 text-center mb-3">— संत तुकाराम महाराज</div>
          <p className="text-gray-600 text-center italic">
            "Let's all help each other and go forward in goodness"
          </p>
        </div>

        <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">The Problem We Solve</h2>
        <p className="text-gray-600 leading-relaxed mb-5">
          We live in vibrant communities filled with incredible events, talented service providers, and amazing
          people. Yet, too often, we miss out on the very experiences that could enrich our lives. That important
          community event? Lost in a sea of WhatsApp messages. That skilled local service provider? Hidden because
          they lack the right channels to reach you.
        </p>

        <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">Our Solution</h2>
        <p className="text-gray-600 leading-relaxed">
          Sahayy bridges this gap by creating a unified platform where communities truly connect. We make it
          effortless for everyone to share events, discover opportunities, and never miss out on what matters.
        </p>
      </section>

      {/* Values */}
      <section className="px-6 py-14 bg-indigo-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-indigo-600 text-xs font-semibold uppercase tracking-widest mb-2">Values</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl border border-indigo-100 px-5 py-5 shadow-sm">
                <div className="text-3xl mb-3">{v.emoji}</div>
                <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="px-6 py-14 max-w-2xl mx-auto text-center">
        <p className="text-indigo-600 text-xs font-semibold uppercase tracking-widest mb-2">Mission</p>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
        <p className="text-gray-600 leading-relaxed text-lg">
          At Sahayy, our mission is to strengthen communities by providing the digital tools that make local
          connections easier, more meaningful, and more productive. We believe that when communities are
          well-connected, everyone benefits.
        </p>
      </section>

      {/* CTA */}
      <section className="px-6 py-12 bg-indigo-600 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Join the Sahayy Community</h2>
        <p className="text-indigo-200 mb-8">Download the app today and start connecting with your local community</p>
        <a
          href="https://chat.sahayy.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-white text-indigo-700 font-semibold px-8 py-3.5 rounded-2xl hover:bg-indigo-50 transition-colors"
        >
          Try for Free
        </a>
      </section>
    </div>
  );
};

export default About;
